import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui/components/sheet';
import { createFileRoute } from '@tanstack/react-router';
import { Calendar, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LimitReachedDialog,
  LoadingState,
  PageContainer,
  PageHeader,
} from '@/components/shared';
import { useCreateOccasion } from '@/hooks/mutations/use-create-occasion';
import { useDeleteOccasion } from '@/hooks/mutations/use-delete-occasion';
import { useUpdateOccasion } from '@/hooks/mutations/use-update-occasion';
import {
  type OccasionItem,
  useRelationship,
} from '@/hooks/queries/use-relationship';
import { useUserMe } from '@/hooks/queries/use-user-me';

import {
  OccasionItemCard,
  OccasionItemForm,
  type OccasionItemFormValues,
} from './-components';

export const Route = createFileRoute('/(app)/occasions/me')({
  component: MyOccasionsPage,
});

const FREE_PLAN_LIMIT = 3;

function MyOccasionsPage() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<OccasionItem | undefined>();
  const [deletingItem, setDeletingItem] = useState<OccasionItem | undefined>();
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
  } = useUserMe();
  const relationshipId = userData?.relationships?.[0]?.id;
  const plan = userData?.plan ?? 'free';

  const {
    data: relationshipData,
    isLoading: isLoadingRelationship,
    error: relationshipError,
    refetch,
  } = useRelationship(relationshipId);

  const createMutation = useCreateOccasion(relationshipId ?? '');
  const updateMutation = useUpdateOccasion(relationshipId ?? '');
  const deleteMutation = useDeleteOccasion(relationshipId ?? '');

  const isLoading = isLoadingUser || isLoadingRelationship;
  const error = userError || relationshipError;

  const occasions = relationshipData?.user_occasions ?? [];
  const isFreePlan = plan === 'free';
  const canAddMore = !isFreePlan || occasions.length < FREE_PLAN_LIMIT;

  const filteredItems = occasions.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleAddClick = () => {
    if (!canAddMore) {
      setShowLimitDialog(true);
      return;
    }
    setEditingItem(undefined);
    setIsSheetOpen(true);
  };

  const handleEdit = (item: OccasionItem) => {
    setEditingItem(item);
    setIsSheetOpen(true);
  };

  const handleDelete = (item: OccasionItem) => {
    setDeletingItem(item);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setEditingItem(undefined);
  };

  const handleSubmit = async (data: OccasionItemFormValues) => {
    try {
      if (editingItem) {
        await updateMutation.mutateAsync({
          occasionId: editingItem.id,
          data,
        });
        toast.success('Ocasião atualizada com sucesso!');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Ocasião adicionada com sucesso!');
      }
      handleCloseSheet();
    } catch {
      toast.error('Ocorreu um erro. Tente novamente.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(deletingItem.id);
      toast.success('Ocasião excluída com sucesso!');
      setDeletingItem(undefined);
    } catch {
      toast.error('Erro ao excluir ocasião. Tente novamente.');
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState message="Carregando suas ocasiões..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorState
          title="Erro ao carregar ocasiões"
          message="Não foi possível carregar suas ocasiões especiais. Tente novamente."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Minhas Ocasiões"
          subtitle={
            isFreePlan
              ? `${occasions.length} de ${FREE_PLAN_LIMIT} ocasiões`
              : `${occasions.length} ocasiões`
          }
        />

        {occasions.length > 0 && (
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Buscar ocasiões..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {occasions.length === 0 ? (
          <EmptyState
            icon={<Calendar className="h-6 w-6" />}
            title="Nenhuma ocasião cadastrada"
            description="Adicione ocasiões especiais como aniversários, dia dos namorados e outras datas importantes."
            action={
              <Button onClick={handleAddClick}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar primeira ocasião
              </Button>
            }
          />
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon={<Search className="h-6 w-6" />}
            title="Nenhum resultado"
            description="Não encontramos ocasiões com esse termo."
          />
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <OccasionItemCard
                key={item.id}
                item={item}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {occasions.length > 0 && (
        <div className="fixed bottom-24 left-1/2 z-40 -translate-x-1/2 md:bottom-8">
          <Button
            onClick={handleAddClick}
            className="rounded-full px-6 shadow-lg"
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar ocasião
          </Button>
        </div>
      )}

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent
          side="bottom"
          className="h-auto max-h-[90vh] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>
              {editingItem ? 'Editar ocasião' : 'Adicionar ocasião'}
            </SheetTitle>
            <SheetDescription>
              {editingItem
                ? 'Atualize as informações da ocasião especial.'
                : 'Adicione uma ocasião especial para lembrar.'}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <OccasionItemForm
              item={editingItem}
              onSubmit={handleSubmit}
              onCancel={handleCloseSheet}
              isLoading={createMutation.isPending || updateMutation.isPending}
            />
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!deletingItem}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingItem(undefined);
          }
        }}
        title="Excluir ocasião"
        description={`Tem certeza que deseja excluir "${deletingItem?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />

      <LimitReachedDialog
        open={showLimitDialog}
        onOpenChange={setShowLimitDialog}
        feature="occasions"
        limit={FREE_PLAN_LIMIT}
      />
    </PageContainer>
  );
}
